/**
 * The document viewer, shared by reception and the guest kiosk.
 *
 * One component, three modes:
 *
 * - `edit`   — the guest fills in the fields (kiosk)
 * - `mirror` — reception watches, read-only, changes pulse as they arrive
 * - `review` — reception corrects values, before or after pushing
 *
 * **The page is the document, not a picture with a form laid over it.** Every
 * value is drawn as text in the print's own typeface, at the size the PMS
 * printed it, on the printed baseline, hanging from the spot the printed value
 * hung from - so an untouched card looks like the paper it came off, and looks
 * the same on the desk as on the tablet. In `review` that text *is* the
 * editor: click into a surname and the caret lands in it; type, and the
 * letters change where they stand, as in a word processor. There is no box,
 * no tint and no pencil. The version before this drew a tinted, ringed input
 * with a pencil in every cell, which made a printed registration card look
 * like a web form and left staff unsure which layer was the document.
 *
 * To draw the live value where the printed one was, the printed one has to
 * go. `printedLayout` finds its ink on the page image, and the viewer papers
 * over exactly that - never a caption, never a rule - and takes the print's
 * size, baseline and position while it is there.
 *
 * Overlays are positioned from page fractions, so the same numbers place a box
 * on a 1240px preview, a 2480px raster and a 595pt PDF. Text is sized in
 * container units of the page's own width, so it scales with the page and not
 * with the window. The legacy client had three different overlay payload
 * shapes with percentage strings, and a long-standing bug where `%%` escaping
 * leaked into the CSS.
 */

import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { ArrowLeftRight, Maximize, PenLine } from 'lucide-react'
import { cn, overlayStyle } from '@/lib/utils'
import type { CheckboxGroup, SessionField, SignatureBox } from '@/lib/types'
import { inputModeFor } from '@/components/domain/FieldControl'
import {
  textStart,
  type Box,
  type FontMetrics,
  type TextPlacement,
} from '@/components/domain/printedLayout'
import { PAGE_FONT, pageFontMetrics, textWidthEm } from '@/components/domain/printedPage'
import {
  analyseImage,
  analyseInWorker,
  analysisKey,
  canReadPixels,
  preloadImage,
  rememberAnalysis,
  settledAnalysis,
  workerAvailable,
  type PageAnalysis,
  type PageGeometry,
} from '@/components/domain/pageAnalysis'

export type ViewerMode = 'edit' | 'mirror' | 'review'

export interface ViewerPage {
  page: number
  image_url: string
  width_px: number
  height_px: number
}

interface Props {
  mode: ViewerMode
  pages: ViewerPage[]
  fields: SessionField[]
  checkboxGroups?: CheckboxGroup[]
  signatures?: SignatureBox[]
  currentPage: number
  onPageChange?: (page: number) => void
  /** A value on the page took focus, so the list beside it can follow. */
  onFieldFocus?: (fieldKey: string) => void
  onFieldTap?: (field: SessionField) => void
  onSignatureTap?: (signature: SignatureBox) => void
  /**
   * Edit on the page itself. Supplying this in `review` makes every value on
   * the page editable text; without it the values are only drawn.
   */
  onFieldChange?: (fieldKey: string, value: string) => void
  /** Tick a box on the page itself. Same rule as `onFieldChange`. */
  onCheckboxToggle?: (groupKey: string, optionKey: string, selected: boolean) => void
  /** Field keys changed since the last render, for the mirror pulse. */
  recentlyChanged?: Set<string>
  /** The guest's words for the viewer's own text; the desk keeps English. */
  labels?: { tapToSign?: string; pageFailed?: string; retry?: string }
  className?: string
}

/** Automatic retries of a page image before the viewer says it failed. */
const IMAGE_RETRIES = 2

export function DocumentViewer({
  mode,
  pages,
  fields,
  checkboxGroups = [],
  signatures = [],
  currentPage,
  onFieldFocus,
  onFieldTap,
  onSignatureTap,
  onFieldChange,
  onCheckboxToggle,
  recentlyChanged,
  labels,
  className,
}: Props) {
  const page = pages.find((p) => p.page === currentPage) ?? pages[0]
  const imageUrl = page?.image_url
  // Each of these names the page image it is about, rather than being a flag
  // an effect resets when the page changes. An image the browser already
  // holds - the same card sent again after a cancel - fires `load` before
  // that effect runs on a tablet, the reset landed after it, and the page sat
  // on the loading shimmer for good.
  //
  // Loaded: the image is on screen. The page shows from here on: the scan
  // already has the printed values where the live ones will go, so it is
  // right to look at while its print is still being read.
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null)
  // A page image that fails is asked for again, then reported. It used to
  // leave the loading shimmer up for good: on a real tablet every page image
  // was a 401 (an <img> cannot send the tablet's Authorization header), and
  // the guest looked at a blank grey card with nothing to say why.
  const [retry, setRetry] = useState<{ url: string; attempt: number } | null>(null)
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const loaded = imageUrl !== undefined && loadedUrl === imageUrl
  const attempt = retry && retry.url === imageUrl ? retry.attempt : 0
  const failed = imageUrl !== undefined && failedUrl === imageUrl
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => () => clearTimeout(retryTimer.current), [imageUrl])

  const pageFields = useMemo(
    () => fields.filter((field) => field.page === page?.page && field.overlay),
    [fields, page?.page],
  )
  const pageCheckboxes = useMemo(
    () =>
      checkboxGroups.flatMap((group) =>
        group.options
          .filter((option) => option.page === page?.page && option.overlay)
          .map((option) => ({ group, option })),
      ),
    [checkboxGroups, page?.page],
  )
  const aspect = page ? (page.height_px || 3508) / (page.width_px || 2480) : 1
  // How far each square's tap area may reach, in page widths: up to the
  // nearest other square, so the stacked Mr / Mrs / Miss on a phone do not
  // overlap and steal each other's taps.
  const tickReach = useMemo(() => {
    const centres = pageCheckboxes.map(({ option }) => {
      const box = option.overlay!
      return { x: box.x + box.w / 2, y: (box.y + box.h / 2) * aspect }
    })
    return centres.map((a, i) =>
      centres.reduce((reach, b, j) => {
        if (i === j) return reach
        return Math.min(reach, Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y)))
      }, Infinity),
    )
  }, [pageCheckboxes, aspect])
  const pageSignatures = useMemo(
    () => signatures.filter((signature) => signature.page === page?.page),
    [signatures, page?.page],
  )

  // Values change on every keystroke; the boxes they sit in do not. Keying the
  // reading on geometry alone keeps typing from re-reading the page.
  const geometry = geometryKeyFor(fields, checkboxGroups, page?.page)
  const boxes = useMemo(() => JSON.parse(geometry) as PageGeometry, [geometry])
  const metrics = pageFontMetrics()
  const key = imageUrl ? analysisKey(imageUrl, geometry) : ''

  // What the print says: from the worker, or read here when there is none.
  const [analysis, setAnalysis] = useState<{ key: string; value: PageAnalysis } | null>(null)
  const [workerFailed, setWorkerFailed] = useState<string | null>(null)
  const inHand = (analysis?.key === key ? analysis.value : settledAnalysis(key)) ?? null
  const onThisThread = !workerAvailable() || workerFailed === key

  // Off the main thread, from the moment the page is known - in parallel with
  // its image arriving, not after it.
  useEffect(() => {
    if (!imageUrl || onThisThread || settledAnalysis(key)) return
    let live = true
    void analyseInWorker(imageUrl, boxes, geometry, metrics).then((value) => {
      if (!live) return
      if (value) setAnalysis({ key, value })
      else setWorkerFailed(key)
    })
    return () => {
      live = false
    }
  }, [imageUrl, onThisThread, key, boxes, geometry, metrics])

  // No worker, or it could not read this page: read the loaded image here,
  // once the page has painted. A browser that will not hand over the pixels
  // at all gets the whole-cell fallback at once.
  useEffect(() => {
    const image = imageRef.current
    if (!onThisThread || inHand || !loaded || !image) return
    const read = () => {
      const value = analyseImage(image, boxes, metrics, aspect)
      rememberAnalysis(key, value)
      setAnalysis({ key, value })
    }
    if (!canReadPixels()) {
      read()
      return
    }
    const timer = setTimeout(read, 32)
    return () => clearTimeout(timer)
  }, [onThisThread, inHand, loaded, key, boxes, metrics, aspect])

  // Turning the page should find it ready: once this page is read, fetch and
  // read the others in the background.
  const pageNo = page?.page
  useEffect(() => {
    if (!inHand || onThisThread) return
    for (const other of pages) {
      if (other.page === pageNo) continue
      preloadImage(other.image_url)
      const otherKey = geometryKeyFor(fields, checkboxGroups, other.page)
      void analyseInWorker(other.image_url, JSON.parse(otherKey) as PageGeometry, otherKey, metrics)
    }
  }, [inHand, onThisThread, pages, pageNo, fields, checkboxGroups, metrics])

  const layout = loaded ? (inHand?.layout ?? null) : null
  const fills = loaded ? (inHand?.fills ?? null) : null

  // The printed values lifted off: the erased rows painted with their own
  // paper, over the scan, before the first paint that shows the live values -
  // an effect would show one frame of both at once.
  const cleanRef = useRef<HTMLCanvasElement>(null)
  useLayoutEffect(() => {
    const ctx = fills && cleanRef.current?.getContext('2d')
    if (!fills || !ctx) return
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)
    for (const { x, y, w, h, rgba } of fills) {
      ctx.fillStyle = `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${rgba[3] / 255})`
      ctx.fillRect(x, y, w, h)
    }
  }, [fills])

  if (!page) {
    return (
      <div className="grid h-64 place-items-center rounded-[12px] border border-[var(--border)] bg-[var(--surface-2)] text-[13px] text-[var(--text-muted)]">
        No page images available for this document.
      </div>
    )
  }

  const interactive = mode === 'edit'
  const editableHere = mode === 'review' && Boolean(onFieldChange)
  // The guest ticks the card's own squares too, as on paper - there is no
  // list of choices under the page.
  const tickableHere = mode !== 'mirror' && Boolean(onCheckboxToggle)

  return (
    <div
      data-page={page.page}
      className={cn(
        'relative isolate w-full overflow-hidden rounded-[12px] border border-[var(--border)] bg-[var(--page-paper)]',
        className,
      )}
      style={{
        // Reserve the right aspect ratio before the image loads, so values do
        // not land in the wrong place for a frame and the layout does not jump.
        aspectRatio: `${page.width_px || 2480} / ${page.height_px || 3508}`,
        // The page's own width is the unit its text is sized in.
        containerType: 'inline-size',
      }}
    >
      <img
        ref={imageRef}
        key={`${page.image_url}#${attempt}`}
        src={attempt ? withRetry(page.image_url, attempt) : page.image_url}
        alt={`Page ${page.page}`}
        className="absolute inset-0 h-full w-full object-contain"
        decoding="async"
        fetchPriority="high"
        onLoad={() => {
          setLoadedUrl(page.image_url)
          setFailedUrl(null)
        }}
        onError={() => {
          const url = page.image_url
          if (attempt < IMAGE_RETRIES) {
            retryTimer.current = setTimeout(
              () => setRetry({ url, attempt: attempt + 1 }),
              1500 * (attempt + 1),
            )
          } else {
            setFailedUrl(url)
          }
        }}
        draggable={false}
      />

      {!loaded && !failed && <div className="skeleton absolute inset-0" aria-hidden />}

      {failed && !loaded && (
        <div
          role="alert"
          className="absolute inset-0 grid place-items-center bg-[var(--surface-2)] p-6 text-center"
        >
          <div>
            <p className="text-[15px] text-[var(--text-muted)]">
              {labels?.pageFailed ?? 'This page could not be loaded.'}
            </p>
            <button
              type="button"
              onClick={() => {
                setFailedUrl(null)
                setRetry({ url: page.image_url, attempt: attempt + 1 })
              }}
              className="mt-4 h-11 rounded-[10px] border border-[var(--border-strong)] bg-[var(--surface)] px-5 text-[14px] font-medium"
            >
              {labels?.retry ?? 'Try again'}
            </button>
          </div>
        </div>
      )}

      {fills && inHand && (
        <canvas
          ref={cleanRef}
          width={inHand.width}
          height={inHand.height}
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full"
        />
      )}

      {layout &&
        pageFields.map((field) => {
          // A box too small to hold a pixel has no placement. Its value is
          // still in the list beside the page.
          const place = layout.text[field.field_key]
          if (!place) return null
          return (
            <PageValue
              key={field.field_key}
              field={field}
              place={place}
              // Lifted off the page image already, unless it could not be read.
              paper={fills ? null : place.erase}
              fontSize={layout.fontSize}
              metrics={metrics}
              aspect={aspect}
              changed={recentlyChanged?.has(field.field_key)}
              onFocus={() => onFieldFocus?.(field.field_key)}
              onChange={
                editableHere ? (value) => onFieldChange?.(field.field_key, value) : undefined
              }
              onTap={
                interactive && field.guest_editable
                  ? () => {
                      onFieldFocus?.(field.field_key)
                      onFieldTap?.(field)
                    }
                  : undefined
              }
            />
          )
        })}

      {layout &&
        pageCheckboxes.map(({ group, option }, index) => {
          const key = tickKey(group.group_key, option.option_key)
          const reach = tickReach[index]
          const tick = layout.ticks[key]
          const canTick = tickableHere && group.guest_editable
          // The print already says what it says. Draw only where the answer
          // now differs from it - and when the print could not be read, clear
          // the square and draw the answer, so there is only ever one.
          const erase = tick.printed !== false && (!option.selected || tick.printed === null)
          const draw = option.selected && tick.printed !== true

          return (
            <Fragment key={key}>
              {erase && <Paper box={tick.interior} />}
              {draw && <TickMark box={tick.interior} />}
              {canTick && (
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={option.selected}
                  aria-label={`${group.label}: ${option.label}`}
                  style={{
                    ...overlayStyle(option.overlay!),
                    ['--tick-reach' as string]: Number.isFinite(reach) ? cq(reach) : '100vw',
                  }}
                  className={cn(
                    'absolute cursor-pointer',
                    // A printed square is a few pixels across on a phone, too
                    // small for a finger: the tap area reaches past it,
                    // invisibly, centred on the square - but never into the
                    // next square's.
                    interactive &&
                      'before:absolute before:left-1/2 before:top-1/2 before:size-[min(max(100%,2.75rem),var(--tick-reach))] before:-translate-x-1/2 before:-translate-y-1/2 before:content-[""]',
                  )}
                  onClick={() =>
                    onCheckboxToggle?.(group.group_key, option.option_key, !option.selected)
                  }
                />
              )}
            </Fragment>
          )
        })}

      {pageSignatures.map((signature) => (
        <button
          key={`${signature.signature_key}-${signature.page}`}
          type="button"
          disabled={!interactive}
          onClick={() => onSignatureTap?.(signature)}
          aria-label={
            signature.captured ? `${signature.label}, signed` : `${signature.label}, not yet signed`
          }
          style={overlayStyle(signature.overlay)}
          className={cn(
            'absolute grid place-items-center rounded-[4px] transition-colors',
            signature.captured
              ? 'overflow-hidden bg-transparent'
              : interactive
                ? // The guest's box: dashed like a form's "sign here", sized in
                  // the page's own units so it grows with the tablet's zoom,
                  // and breathing gently until it is signed.
                  'kiosk-attention cursor-pointer rounded-[6px] border-2 border-dashed border-[var(--brand)] bg-[var(--brand-subtle)]/80 hover:bg-[var(--brand-subtle)]'
                : 'bg-[var(--brand)]/8 ring-1 ring-dashed ring-[var(--brand)]',
          )}
        >
          {signature.captured && signature.thumb_url ? (
            <SignatureInk src={signature.thumb_url} alt={signature.label} />
          ) : signature.captured ? null : interactive ? (
            <span className="inline-flex items-center gap-[0.5em] px-1 text-[1.9cqw] font-semibold text-[var(--brand)]">
              <PenLine className="h-[1.15em] w-[1.15em] shrink-0" aria-hidden />
              {labels?.tapToSign ?? 'Tap to sign'}
            </span>
          ) : (
            <span className="px-1 text-[clamp(8px,1vw,12px)] font-medium text-[var(--brand)]">
              {signature.label}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

/**
 * A captured signature, cropped to its ink and scaled to fill the box as far
 * as its shape allows - what the stamped PDF does, so the screen matches it.
 *
 * The stored PNG is the whole pad, and a phone's pad is nearly square: drawn
 * as is, the empty pad around the strokes shrank the signature to a speck in
 * a short, wide box. The pad crops before upload now, but signatures saved
 * before that (or by a tablet still running an older bundle) are not, so the
 * crop happens here too. Until it is ready, and if the pixels cannot be read,
 * the image is drawn uncropped - small, but inside the box.
 *
 * Pinned to the box, not laid out in it: as a grid item the image grew the
 * row to its own aspect ratio and spilled above and below the box.
 */
function SignatureInk({ src, alt }: { src: string; alt: string }) {
  const [cropped, setCropped] = useState<{ src: string; url: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    let url: string | null = null
    const image = new Image()
    image.onload = () => {
      cropToInk(image)?.toBlob((blob) => {
        if (!blob) return
        url = URL.createObjectURL(blob)
        if (cancelled) URL.revokeObjectURL(url)
        else setCropped({ src, url })
      }, 'image/png')
    }
    image.src = src
    return () => {
      cancelled = true
      image.onload = null
      if (url) URL.revokeObjectURL(url)
    }
  }, [src])

  return (
    <img
      src={cropped?.src === src ? cropped.url : src}
      alt={alt}
      className="pointer-events-none absolute inset-0 h-full w-full object-contain"
    />
  )
}

/** The image cut down to its non-transparent pixels, or null if it cannot be. */
function cropToInk(image: HTMLImageElement): HTMLCanvasElement | null {
  const width = image.naturalWidth
  const height = image.naturalHeight
  if (!width || !height) return null
  const source = document.createElement('canvas')
  source.width = width
  source.height = height
  const ctx = source.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null
  ctx.drawImage(image, 0, 0)
  let alpha: Uint8ClampedArray
  try {
    alpha = ctx.getImageData(0, 0, width, height).data
  } catch {
    return null // a tainted canvas: leave the image as it is
  }
  let top = height
  let left = width
  let bottom = -1
  let right = -1
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (alpha[(y * width + x) * 4 + 3] === 0) continue
      if (y < top) top = y
      if (y > bottom) bottom = y
      if (x < left) left = x
      if (x > right) right = x
    }
  }
  if (bottom < 0) return null
  const crop = document.createElement('canvas')
  crop.width = right - left + 1
  crop.height = bottom - top + 1
  crop
    .getContext('2d')
    ?.drawImage(source, left, top, crop.width, crop.height, 0, 0, crop.width, crop.height)
  return crop
}

/** The same file, asked for again past any cached failure. */
function withRetry(url: string, attempt: number) {
  return `${url}${url.includes('?') ? '&' : '?'}retry=${attempt}`
}

function tickKey(groupKey: string, optionKey: string) {
  return `${groupKey}:${optionKey}`
}

/**
 * The boxes a page's print is read in, as a stable string: the cache key for
 * its reading, and the same for the page on screen and a page read ahead.
 */
function geometryKeyFor(
  fields: SessionField[],
  checkboxGroups: CheckboxGroup[],
  pageNo: number | undefined,
): string {
  const geometry: PageGeometry = {
    text: fields
      .filter((field) => field.page === pageNo && field.overlay)
      .map((field) => ({ key: field.field_key, box: field.overlay! })),
    ticks: checkboxGroups.flatMap((group) =>
      group.options
        .filter((option) => option.page === pageNo && option.overlay)
        .map((option) => ({
          key: tickKey(group.group_key, option.option_key),
          box: option.overlay!,
        })),
    ),
  }
  return JSON.stringify(geometry)
}

/**
 * One value, drawn as the document's own text.
 *
 * Three elements that share one geometry, so a value sits in exactly the same
 * place whether it is being typed into, tapped, or only watched:
 *
 * - an `<input>` on the desk, borderless and see-through, so clicking the text
 *   puts the caret in the text and native selection, undo and word-jumping
 *   all behave as they do anywhere else;
 * - a `<button>` on the tablet, which opens the guest's field sheet - a
 *   finger cannot place a caret in 8pt type;
 * - plain text everywhere else.
 *
 * Each takes its direction from its own text (`dir="auto"`), never from the
 * screen's: the page is a left-to-right print, and on the Arabic tablet an
 * inherited right-to-left turned "155.25 AED" into "AED 155.25" and
 * "16 April 2024" into "April 2024 16". An Arabic name still reads right to
 * left, because its first letter says so.
 *
 * While the input has focus it shows what is being typed, not what the server
 * last echoed: a correction is saved as it is made and the server may
 * normalise it, and rewriting a value under someone's caret is how a
 * keystroke lands in the wrong place. It shows the stored value again on blur.
 */
function PageValue({
  field,
  place,
  paper,
  fontSize,
  metrics,
  aspect,
  changed,
  onFocus,
  onChange,
  onTap,
}: {
  field: SessionField
  place: TextPlacement
  paper: Box | null
  fontSize: number
  metrics: FontMetrics
  aspect: number
  changed?: boolean
  onFocus: () => void
  onChange?: (value: string) => void
  onTap?: () => void
}) {
  const [draft, setDraft] = useState<string | null>(null)
  const original = useRef('')
  const shown = draft ?? field.value
  const style = valueStyle(field.overlay!, place, fontSize, metrics, aspect, shown)
  const ink = cn(
    'absolute block overflow-hidden whitespace-pre bg-transparent text-left',
    'text-[var(--page-print)]',
    changed && 'pulse-change',
  )
  const listId = field.options?.length ? `page-options-${field.field_key}` : undefined

  return (
    <>
      {paper && <Paper box={paper} />}

      {onChange ? (
        <>
          <input
            type="text"
            dir="auto"
            value={shown}
            aria-label={field.label}
            data-field={field.field_key}
            inputMode={inputModeFor(field.data_type)}
            maxLength={field.max_length ?? undefined}
            list={listId}
            spellCheck={false}
            autoComplete="off"
            // The caret is the focus indicator, as it is in any document. A
            // text input matches :focus-visible on a mouse click too, so the
            // global ring would draw a box round every value that is clicked.
            style={{ ...style, outline: 'none', caretColor: 'var(--page-print)' }}
            className={cn(ink, 'cursor-text')}
            onFocus={() => {
              original.current = field.value
              setDraft(field.value)
              onFocus()
            }}
            onChange={(event) => {
              setDraft(event.target.value)
              onChange(event.target.value)
            }}
            onBlur={() => setDraft(null)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                event.currentTarget.blur()
              }
              if (event.key === 'Escape') {
                event.preventDefault()
                if (shown !== original.current) onChange(original.current)
                event.currentTarget.blur()
              }
            }}
          />
          {listId && (
            <datalist id={listId}>
              {field.options!.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          )}
        </>
      ) : onTap ? (
        <button
          type="button"
          dir="auto"
          aria-label={`${field.label}: ${shown || 'empty'}`}
          data-field={field.field_key}
          style={style}
          className={cn(ink, 'cursor-pointer')}
          onClick={onTap}
        >
          {shown}
        </button>
      ) : (
        <span dir="auto" style={style} className={ink} data-field={field.field_key}>
          {shown}
        </span>
      )}
    </>
  )
}

/**
 * Where a value's text goes, in container units of the page width.
 *
 * The element spans its cell, so the whole cell is the click target - an
 * empty "Address" has nothing else to click. Inside it, padding puts the text
 * on the printed baseline and at the printed start: the content box is
 * exactly one line of the page font tall, and both an input and a line box
 * centre their text in it, so the baseline lands `ascent` below its top
 * whichever of the three elements is drawing it.
 */
function valueStyle(
  box: Box,
  place: TextPlacement,
  fontSize: number,
  metrics: FontMetrics,
  aspect: number,
  text: string,
): CSSProperties {
  const baseline = place.baseline * aspect
  const lineTop = baseline - metrics.ascent * fontSize
  const lineBottom = baseline + metrics.descent * fontSize
  const top = Math.min(box.y * aspect, lineTop)
  const bottom = Math.max((box.y + box.h) * aspect, lineBottom)
  const start = textStart(place, box, textWidthEm(text) * fontSize)

  return {
    left: cq(box.x),
    width: cq(box.w),
    top: cq(top),
    height: cq(bottom - top),
    paddingTop: cq(lineTop - top),
    paddingBottom: cq(bottom - lineBottom),
    paddingLeft: cq(start - box.x),
    paddingRight: 0,
    fontFamily: PAGE_FONT,
    fontSize: cq(fontSize),
    lineHeight: metrics.ascent + metrics.descent,
    // Nothing the app's own type inherits: the width the text was placed by
    // is measured in plain Arial, and a tracked or tabular value would not
    // be the width it was placed at.
    fontWeight: 400,
    letterSpacing: 'normal',
    fontFeatureSettings: 'normal',
    fontVariantNumeric: 'normal',
  }
}

/** A length given as a fraction of the page width. */
function cq(fraction: number) {
  return `${(fraction * 100).toFixed(4)}cqw`
}

/** Blank paper, over ink that is no longer the document's answer. */
function Paper({ box }: { box: Box }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute bg-[var(--page-paper)]"
      style={overlayStyle(box)}
    />
  )
}

/**
 * A tick in the print's own hand: the heavy check the PMS draws, traced off
 * the registration card and scaled to the inside of the square.
 */
const TICK = [
  [0, 0.417],
  [0.067, 0.333],
  [0.233, 0.517],
  [0.667, 0],
  [0.833, 0],
  [0.867, 0.167],
  [0.333, 0.833],
  [0.133, 0.833],
  [0, 0.583],
]
  .map((point) => point.join(','))
  .join(' ')

function TickMark({ box }: { box: Box }) {
  return (
    <svg
      aria-hidden
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      className="pointer-events-none absolute"
      style={overlayStyle(box)}
    >
      <polygon points={TICK} fill="var(--page-print)" />
    </svg>
  )
}

export function PageNavigator({
  pages,
  current,
  onChange,
  className,
}: {
  pages: { page: number }[]
  current: number
  onChange: (page: number) => void
  className?: string
}) {
  if (pages.length <= 1) return null
  return (
    <nav aria-label="Pages" className={cn('flex items-center justify-center gap-1.5', className)}>
      {pages.map(({ page }) => (
        <button
          key={page}
          onClick={() => onChange(page)}
          aria-current={page === current ? 'page' : undefined}
          className={cn(
            'h-8 min-w-8 rounded-[8px] px-2 text-[13px] transition-colors',
            page === current
              ? 'bg-[var(--brand)] font-medium text-[var(--on-brand)]'
              : 'bg-[var(--surface-2)] text-[var(--text-muted)] hover:bg-[var(--border)]',
          )}
        >
          {page}
        </button>
      ))}
    </nav>
  )
}

/** How a page is sized on screen: as wide as its column, or whole in it. */
export type PageFit = 'width' | 'page'

const FIT_KEY = 'ereg.viewer.fit'

function readFit(): PageFit {
  try {
    return localStorage.getItem(FIT_KEY) === 'page' ? 'page' : 'width'
  } catch {
    return 'width'
  }
}

/**
 * The page's sizing, and the box it scrolls in.
 *
 * "Width" is the default: the page is as wide as its column and scrolls, so
 * the print is readable. A whole A4 page fitted to a screen's height was
 * about half its printed size at 1080p, and smaller than that on a laptop.
 * "Page" fits it whole, for the receptionist who wants to see the card at a
 * glance. The choice is remembered per browser.
 *
 * `width` is the column width at which the page stands whole in the box, for
 * the "page" layout. The box's height is set by the layout around it (a
 * screen-tall pane), never by the page inside it, so measuring it cannot feed
 * back into itself.
 */
export function usePageFit(
  page: { page?: number; width_px: number; height_px: number } | undefined,
) {
  const ref = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState(0)
  const [mode, setModeState] = useState<PageFit>(readFit)

  useLayoutEffect(() => {
    const box = ref.current
    if (!box) return
    // Measured once now as well: ResizeObserver reports on the next rendered
    // frame, and a tab that is not painting never has one.
    setHeight(box.clientHeight)
    const observer = new ResizeObserver(([entry]) => setHeight(entry.contentRect.height))
    observer.observe(box)
    return () => observer.disconnect()
  }, [])

  // A new page starts at its top, not wherever the last one was scrolled to.
  useEffect(() => {
    ref.current?.scrollTo({ top: 0 })
  }, [page?.page])

  const setMode = useCallback((next: PageFit) => {
    setModeState(next)
    try {
      localStorage.setItem(FIT_KEY, next)
    } catch {
      // Private window or blocked storage: the choice lasts until reload.
    }
  }, [])

  /** Bring a field's value on the page into view, once the page has drawn it. */
  const reveal = useCallback((fieldKey: string) => {
    requestAnimationFrame(() => {
      const target = ref.current?.querySelector(`[data-field="${CSS.escape(fieldKey)}"]`)
      target?.scrollIntoView({ block: 'center', inline: 'nearest' })
    })
  }, [])

  const aspect = page ? (page.width_px || 2480) / (page.height_px || 3508) : 2480 / 3508
  // Floored, and the border the viewer draws taken off: a width a fraction
  // too generous makes a page a pixel taller than its box, and the box grows a
  // scrollbar for one pixel.
  const width = height > 0 ? Math.floor((height - 2) * aspect) + 2 : null
  return { ref, width, mode, setMode, reveal }
}

/** The two-way switch between a readable page and a whole one. */
export function PageFitToggle({
  mode,
  onChange,
  className,
}: {
  mode: PageFit
  onChange: (mode: PageFit) => void
  className?: string
}) {
  const options = [
    { value: 'width' as const, label: 'Fit width', icon: ArrowLeftRight },
    { value: 'page' as const, label: 'Whole page', icon: Maximize },
  ]
  return (
    <div
      role="group"
      aria-label="Page size"
      className={cn(
        'inline-flex items-center rounded-[8px] border border-[var(--border)] p-0.5',
        className,
      )}
    >
      {options.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          type="button"
          aria-pressed={mode === value}
          title={label}
          onClick={() => onChange(value)}
          className={cn(
            'inline-flex h-7 items-center gap-1.5 rounded-[6px] px-2 text-[12px] transition-colors',
            mode === value
              ? 'bg-[var(--surface-2)] font-medium text-[var(--text)]'
              : 'text-[var(--text-muted)] hover:text-[var(--text)]',
          )}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden />
          <span className="hidden 2xl:inline">{label}</span>
        </button>
      ))}
    </div>
  )
}
